import * as React from 'react';
import {
  safeCommentLink,
  type CommentDocument,
  type CommentNode,
} from 'lib/comments';

export const COMMENT_TEXT_STYLES =
  'break-words text-sm leading-relaxed text-primary [&_p]:my-2 [&_a]:text-brand-secondary [&_a]:underline [&_blockquote]:my-2 [&_blockquote]:border-s-2 [&_blockquote]:border-secondary [&_blockquote]:ps-3 [&_blockquote]:text-tertiary [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-secondary [&_pre]:p-3 [&_code]:font-mono [&_code]:text-xs [&_h1]:text-xl [&_h2]:text-lg [&_h3]:text-base [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_hr]:my-3 [&_hr]:border-secondary';

export function CommentContent({ document }: { document: CommentDocument }) {
  return (
    <div className={COMMENT_TEXT_STYLES}>
      {document.content.map((node, index) => (
        <React.Fragment key={index}>{renderNode(node)}</React.Fragment>
      ))}
    </div>
  );
}

function renderNode(node: CommentNode): React.ReactNode {
  if (node.type === 'text') {
    let child: React.ReactNode = node.text;
    for (const mark of node.marks ?? []) {
      if (mark.type === 'bold') child = <strong>{child}</strong>;
      if (mark.type === 'italic') child = <em>{child}</em>;
      if (mark.type === 'underline') child = <u>{child}</u>;
      if (mark.type === 'strike') child = <s>{child}</s>;
      if (mark.type === 'code') child = <code>{child}</code>;
      const href =
        mark.type === 'link' ? safeCommentLink(mark.attrs?.href) : undefined;
      if (href)
        child = (
          <a href={href} target="_blank" rel="nofollow ugc noopener noreferrer">
            {child}
          </a>
        );
    }
    return child;
  }
  const children = node.content?.map((child, index) => (
    <React.Fragment key={index}>{renderNode(child)}</React.Fragment>
  ));
  switch (node.type) {
    case 'paragraph':
      return <p>{children}</p>;
    case 'heading':
      return React.createElement(
        `h${Math.max(1, Math.min(6, node.attrs?.level ?? 2))}`,
        {},
        children,
      );
    case 'bulletList':
      return <ul>{children}</ul>;
    case 'orderedList':
      return <ol start={node.attrs?.start}>{children}</ol>;
    case 'listItem':
      return <li>{children}</li>;
    case 'blockquote':
      return <blockquote>{children}</blockquote>;
    case 'codeBlock':
      return (
        <pre>
          <code>{children}</code>
        </pre>
      );
    case 'hardBreak':
      return <br />;
    case 'horizontalRule':
      return <hr />;
    default:
      return children;
  }
}
